import { createAsyncThunk } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "@/app/store";
import { connection, setConnection, log } from "./shared";
import { restartSignalRConnection } from "./restartSignalRConnection";
import { startSignalRConnection } from "./startSignalRConnection";

export const joinSignalRRoom = createAsyncThunk<
  { roomId: string },
  string,
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>("signalr/joinRoom", async (roomId, { rejectWithValue, dispatch }) => {
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 1000;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (!connection) {
      log("joinSignalRRoom: no active connection, restarting", {
        roomId,
        attempt,
      });
      const result = await dispatch(restartSignalRConnection("notifications"));
      if (startSignalRConnection.rejected.match(result)) {
        if (attempt < MAX_RETRIES) {
          log("joinSignalRRoom: restart failed, will retry", {
            roomId,
            attempt,
          });
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          continue;
        }
        return rejectWithValue(result.payload || "Connection not established");
      }
    }

    try {
      log("joinSignalRRoom: invoking JoinRoomAsync", { roomId, attempt });
      const fullRoomId = `workflow:${roomId}`;

      // Wait for either RoomJoined or RoomAccessDenied event
      const result = await new Promise<boolean>((resolve, reject) => {
        let resolved = false;
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            reject(new Error("Room join timeout"));
          }
        }, 10000);

        const cleanup = () => {
          clearTimeout(timeout);
          connection?.off("RoomJoined", joinHandler);
          connection?.off("RoomAccessDenied", deniedHandler);
        };

        const joinHandler = (eventRoomId: string) => {
          if (eventRoomId === fullRoomId && !resolved) {
            resolved = true;
            cleanup();
            log("event: RoomJoined", { eventRoomId, fullRoomId });
            resolve(true);
          }
        };

        const deniedHandler = (eventRoomId: string) => {
          if (eventRoomId === fullRoomId && !resolved) {
            resolved = true;
            cleanup();
            log("event: RoomAccessDenied", { eventRoomId, fullRoomId });
            resolve(false);
          }
        };

        connection?.on("RoomJoined", joinHandler);
        connection?.on("RoomAccessDenied", deniedHandler);

        connection?.invoke("JoinRoomAsync", fullRoomId).catch((err) => {
          if (!resolved) {
            resolved = true;
            cleanup();
            reject(err);
          }
        });
      });

      if (!result) {
        log("joinSignalRRoom: access denied", { roomId });
        return rejectWithValue("Access denied to room");
      }

      log("joinSignalRRoom: successfully joined", { roomId, attempt });
      return { roomId };
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      log("joinSignalRRoom: ERROR", { roomId, message, attempt });

      if (message.includes("Access denied")) {
        return rejectWithValue(message || `Failed to join room ${roomId}`);
      }

      if (attempt < MAX_RETRIES) {
        log("joinSignalRRoom: will retry after delay", {
          roomId,
          attempt,
          delay: RETRY_DELAY,
        });
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        setConnection(null);
        continue;
      }

      return rejectWithValue(message || `Failed to join room ${roomId}`);
    }
  }

  return rejectWithValue(
    `Failed to join room ${roomId} after ${MAX_RETRIES} retries`,
  );
});
